import { useCallback, useEffect, useState } from 'react';
import { Button, Form, FormInstance, Input, Select, Spin } from 'antd';
import {
	FunctionaryInfo,
	UserRole,
} from '../shared/VolunteerApplicationServiceTypes';
import { useTranslation } from 'react-i18next';
import {
	FunctionaryType,
	VALID_ABHYASI_ID_REGEX,
	VALID_STRING_INPUT_REGEX,
} from '../shared/Constants';
import { useRedirectToLogin } from '../hooks/useRedirectToLogin';
import { OptionData, useZonesDropdown } from '../hooks/useZonesDropdown';
import LeftArrowWhite from '../assets/images/arrow-left-white.svg';
import Icon from './ui-components/Icon';

interface FunctionaryDetailsFormProps {
	form: FormInstance;
	functionary?: FunctionaryInfo;
	isEditForm?: boolean;
	onAddEditFunctionarySubmit?: () => void;
	onCancelClick?: () => void;
	submitButtonDisabled?: boolean;
	functionarySrcmIdParam?: string;
}

export const FunctionaryDetailsForm: React.FC<FunctionaryDetailsFormProps> = ({
	form,
	functionary,
	isEditForm,
	onAddEditFunctionarySubmit,
	onCancelClick,
	submitButtonDisabled,
	functionarySrcmIdParam,
}: FunctionaryDetailsFormProps) => {
	const { t } = useTranslation();
	const { role } = useRedirectToLogin();
	const {
		handleZonesSearch,
		zoneoptions,
		zonesloading,
		handleHomeZoneSearch,
		homeZoneOptions,
	} = useZonesDropdown();
	const [zones, setZones] = useState<Array<{ value: string }> | undefined>([]);
	const [homeZone, setHomeZone] = useState<{ value: string } | undefined>();
	const [functionaryTypeValue, setFunctionaryTypeValue] = useState<
		string | undefined
	>();
	const [statusValue, setStatusValue] = useState<boolean | undefined>();
	const [isFunctionaryCareTeam, setIsFunctionaryCareTeam] = useState(
		functionary?.isCareTeam || false
	);

	const showFunctionaryType = role === UserRole.ADMIN;
	const disableSrcmIdField = functionarySrcmIdParam !== '-1';

	const functionaryTypeOptions: OptionData[] = [
		{ label: `${t('isProposer')}`, value: FunctionaryType.IS_PROPOSER },
		{ label: `${t('isApprover')}`, value: FunctionaryType.IS_APPROVER },
		{
			label: `${t('isZC')}`,
			value: FunctionaryType.IS_ZC,
		},
		{ label: `${t('isCareTeam')}`, value: FunctionaryType.IS_CARE_TEAM },
		{
			label: `${t('isPreworkAdmin')}`,
			value: FunctionaryType.IS_PREWORK_ADMIN,
		},
	];

	const handleZoneChange = useCallback((value: Array<{ value: string }>) => {
		const zs = value.map((z) => z.value);
		const zonesList = zs?.map((s) => ({
			value: s,
		}));
		setZones(zonesList);
	}, []);

	const handleHomeZoneChange = useCallback((value: { value: string }) => {
		setHomeZone(value);
	}, []);

	const handleStatusChange = useCallback((value: boolean) => {
		setStatusValue(value);
	}, []);

	const handleFunctionaryTypeChange = useCallback((value: string) => {
		setFunctionaryTypeValue(value);
		setIsFunctionaryCareTeam(value === FunctionaryType.IS_CARE_TEAM);
	}, []);

	useEffect(() => {
		if (functionary?.functionaryType === 'IS_CARE_TEAM') {
			setIsFunctionaryCareTeam(true);
		} else {
			setIsFunctionaryCareTeam(false);
		}
	}, [functionary?.functionaryType]);

	return (
		<Form
			name="basic"
			layout="vertical"
			size="middle"
			labelCol={{ span: 16 }}
			initialValues={{ remember: true }}
			autoComplete="off"
			form={form}
		>
			<div className="relative flex items-center justify-center border-b-[2px] border-gray-700 pb-6 mt-4">
				{/* Back Button - aligned to the left */}
				<div className="hidden md:block">
					<Button
						type="primary"
						onClick={onCancelClick}
						className="absolute left-0 top-0 font-bold text-sm flex items-center gap-2"
					>
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</Button>
				</div>

				{/* Title - centered */}
				<div className="text-center font-sans font-bold text-lg">
					{functionarySrcmIdParam !== '-1'
						? showFunctionaryType
							? t('editFunctinary')
							: t('editProposer')
						: showFunctionaryType
						? t('addFunctionary')
						: t('addProposer')}
				</div>
			</div>
			<div className="flex sm:justify-left md:justify-center md:items-left">
				<div className="bg-transparent md:bg-white sm:p-0 md:p-8 sm:rounded-none md:rounded-lg sm:border-none md:border md:border-solid md:border-[#d9d9d9] md:w-full md:max-w-4xl">
					<div className="flex flex-wrap overflow-y-auto pb-[80px] md:pb-[10px]">
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-[#4C4748] font-sans">
										{t('name')}
										<span className="text-[#ff0000]"> *</span>
									</span>
								}
								name="printName"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_STRING_INPUT_REGEX,
										message: <span>{t('onlyAllowedSpecialCharacters')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={100}
									className="flex min-w-[200px]"
									size="large"
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-[#4C4748]  font-sans">
										{t('email')}
										<span className="text-[#ff0000]"> *</span>
									</span>
								}
								name="email"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
										message: <span>{t('invalidEmail')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input className="flex min-w-[200px]" size="large" />
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-[#4C4748]  font-sans">
										{t('phoneNo')}
										<span className="text-[#ff0000]"> *</span>
									</span>
								}
								name="mobile"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: /^([+]\d{2})?\d{10}$/,
										message: <span>{t('invalidPhoneNo')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input className="flex min-w-[200px]" size="large" />
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-[#4C4748] font-sans">
										{t('SRCMID')}
										<span className="text-[#ff0000]"> *</span>
									</span>
								}
								name="srcmId"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_ABHYASI_ID_REGEX,
										message: <span>{t('invalidSRCMID')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={20}
									disabled={disableSrcmIdField}
									className="flex min-w-[200px]"
									size="large"
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-[#4C4748] font-sans">
										{t('gender')}
										<span className="text-[#ff0000]"> *</span>
									</span>
								}
								name="gender"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Select
									className="flex min-w-[200px]"
									options={[
										{ value: 'MALE', label: `${t('male')}` },
										{ value: 'FEMALE', label: `${t('female')}` },
									]}
									size="large"
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-[#4C4748] font-sans">
										{t('homeZone')}
									</span>
								}
								name="homeZoneToDisplay"
								className="mb-[16px]"
							>
								<Select
									showSearch
									defaultActiveFirstOption={false}
									showArrow={false}
									filterOption={false}
									placeholder={t('searchZones')}
									notFoundContent={zonesloading ? <Spin size="small" /> : null}
									onSearch={handleHomeZoneSearch}
									onChange={handleHomeZoneChange}
									labelInValue
									value={homeZone}
									options={homeZoneOptions}
									className="flex min-w-[200px]"
									size="large"
								/>
							</Form.Item>
						</div>
						{isEditForm && (
							<div className="w-full md:w-1/2 px-2 mb-4">
								<Form.Item
									label={<span className="text-[#4C4748]">{t('status')}</span>}
									name="isActive"
									className="mb-[16px] font-sans"
								>
									<Select
										onChange={handleStatusChange}
										value={statusValue}
										options={[
											{ value: true, label: `${t('active')}` },
											{ value: false, label: `${t('inactive')}` },
										]}
										className="flex min-w-[200px]"
										size="large"
									/>
								</Form.Item>
							</div>
						)}
						{showFunctionaryType && (
							<div className="w-full md:w-1/2 px-2 mb-4">
								<Form.Item
									label={
										<span className="text-[#4C4748] font-sans">
											{t('functionaryType')}
											<span className="text-[#ff0000]"> *</span>
										</span>
									}
									name="functionaryType"
									rules={[
										{
											required: true,
											message: <span>{t('required')}</span>,
										},
									]}
									required={false}
									className="mb-[16px]"
								>
									<Select
										placeholder={t('functionaryType')}
										onChange={handleFunctionaryTypeChange}
										value={functionaryTypeValue}
										options={functionaryTypeOptions}
										className="flex min-w-[200px]"
										size="large"
									/>
								</Form.Item>
							</div>
						)}
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-[#4C4748] font-sans">
										{t('zonesAuthorized')}
										{isFunctionaryCareTeam ? null : (
											<span className="text-[#ff0000]"> *</span>
										)}
									</span>
								}
								name="zone"
								rules={[
									{
										required: isFunctionaryCareTeam ? false : true,
										message: <span>{t('required')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Select
									showSearch
									mode="multiple"
									defaultActiveFirstOption={false}
									showArrow={false}
									filterOption={false}
									placeholder={t('searchZones')}
									notFoundContent={zonesloading ? <Spin size="small" /> : null}
									onSearch={handleZonesSearch}
									onChange={handleZoneChange}
									labelInValue
									disabled={isFunctionaryCareTeam}
									value={zones}
									options={zoneoptions}
									dropdownStyle={{ minHeight: 40 }}
									size="large"
								/>
							</Form.Item>
						</div>
					</div>
					<style>
						{`
							.mobile-top-shadow {
								box-shadow: 0 -4px 6px rgba(133, 133, 133, 0.12);
							}
							@media (min-width: 768px) {
								.mobile-top-shadow {
									box-shadow: none;
								}
							}
						`}
					</style>
					<div className="flex flex-row py-[24px] px-[16px] fixed md:relative bottom-0 left-0 right-0 md:p-[8px] justify-center md:justify-end bg-[#fff] md:bg-transparent mt-[16px] md:mt-[12px] mb-[8px] md:mb-[0px] mobile-top-shadow">
						<Button
							onClick={onCancelClick}
							className="w-[180px] h-[36px] md:w-[140px] border-solid border-[#003399] text-[#003399]"
							variant="outlined"
						>
							{t('cancel')}
						</Button>
						<Button
							onClick={onAddEditFunctionarySubmit}
							type="primary"
							disabled={submitButtonDisabled}
							className="w-[180px] h-[36px] md:w-[140px] ml-[16px]"
						>
							{t('submit')}
						</Button>
					</div>
				</div>
			</div>
		</Form>
	);
};
